/**
 * Help Page - Admin Guide for Tammy
 * 
 * Explains what each admin section does in simple terms.
 * Includes quick tips and common tasks.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HiHome,
  HiShoppingBag,
  HiTag,
  HiClipboardList,
  HiUsers,
  HiPhotograph,
  HiGlobe,
  HiCog,
  HiChevronDown,
  HiChevronUp,
  HiLightBulb,
  HiPhone,
  HiCollection,
  HiAdjustments,
  HiSparkles,
  HiDocumentReport,
  HiViewGrid,
  HiFolder,
  HiInbox,
  HiLink,
  HiSearch
} from 'react-icons/hi';
import { helpShowUpOnGoogleSteps } from '../utils/googleRankingChecklist';

// Help sections for each admin page
const helpSections = [
  {
    id: 'dashboard',
    icon: HiHome,
    title: 'Dashboard',
    color: 'purple',
    link: '/admin',
    description: 'Your home base! See everything at a glance. A "Need attention" card shows new orders, contact messages, and promos ending soon.',
    useFor: [
      'Getting a quick overview when you log in',
      'Seeing "Need attention" alerts (new orders, contact messages, promos ending in 7 days)',
      'Seeing your business hours',
      'Checking what tasks are left to do',
      'Quick links to common actions'
    ]
  },
  {
    id: 'menu',
    icon: HiShoppingBag,
    title: 'Menu',
    color: 'amber',
    link: '/admin/menu',
    description: 'Manage all your bakery items - cakes, cookies, tarts, etc. Duplicate items or collections; bulk price change and reorder by category.',
    useFor: [
      'Add new items (click + Add Item)',
      'Duplicate an item (copy icon) to create "Copy of …" then edit',
      'Mark items gluten free: Edit item → check "🌾 Gluten free" → Save (customers see badge + filter)',
      'Bulk price change: choose category, enter % (e.g. 5 or -10), click Apply',
      'Reorder items: "Reorder by category" → pick category, move up/down, Save order',
      'Edit prices or descriptions',
      'Hide items when sold out (click Hide)',
      'Delete items you no longer sell (trash icon)',
      'If Delete fails: the item is likely used by a Fundraiser campaign — open that campaign, pick a different product or delete the campaign, then try again. Or just use Hide.',
      'Upload new product photos — menu cards stay square; the product page shows the full photo unless you pick Fill square under the preview'
    ],
    quickTip: 'Sold out? Click Hide (you can Show later). Can\'t delete? Check Fundraiser campaigns — a campaign may still point at that product; change the product or delete the campaign first.'
  },
  {
    id: 'categories',
    icon: HiFolder,
    title: 'Categories',
    color: 'amber',
    link: '/admin/categories',
    description: 'Add, edit, or hide menu categories (e.g. Valentines, Seasonal, Gluten Free). Controls how items are grouped on the menu.',
    useFor: [
      'Add new categories (Valentines, Seasonal, Pastries, etc.)',
      'Edit category name or icon (emoji)',
      'Change display order (lower number = first on menu)',
      'Hide a category (won\'t show on public menu; items keep the category)'
    ],
    quickTip: 'New category? Categories → Add Category → name and optional emoji (e.g. 💝). Then add menu items and pick that category.'
  },
  {
    id: 'holidays',
    icon: HiSparkles,
    title: 'Holidays',
    color: 'amber',
    link: '/admin/holidays',
    description: 'One switch turns a whole holiday on or off — homepage hero, special menu, banner, and items.',
    useFor: [
      'Go to Holidays in the sidebar',
      'Open Thanksgiving or Christmas (or create a new holiday)',
      'Set start/end dates and add items with prices and order-by dates',
      'Use Preview, then flip the big ON/OFF switch when ready',
      'Next year: Duplicate next year (or Roll dates), update prices, turn ON',
    ],
    quickTip: 'Christmas 2026 is an empty kit (creatives from Oct 20, go live Nov 1). Add items and images first — do not invent prices. Flip ON only when ready.',
  },
  {
    id: 'collections',
    icon: HiCollection,
    title: 'Special Menus',
    color: 'purple',
    link: '/admin/collections',
    description: 'Create themed menus like "Valentine\'s Day" or "Birthday Parties" and show them on your website. Duplicate a collection to copy name, items, and settings.',
    useFor: [
      'Create seasonal menus (Valentine\'s, Christmas, etc.)',
      'Duplicate a collection (copy icon) to make "Copy of …" with same items; edit and turn on when ready',
      'Add a banner image that shows on the collection page',
      'Add existing products to a collection',
      'Create new products directly from a collection',
      'Turn collections on/off to show/hide on your website',
      'Set dates to automatically show/hide collections',
      'Show collections on your homepage'
    ],
    quickTip: 'Special Menus are perfect for seasonal specials! Duplicate last year\'s menu, update dates and items, then turn it ON.'
  },
  {
    id: 'product-options',
    icon: HiAdjustments,
    title: 'Product Options',
    color: 'indigo',
    link: '/admin/product-options',
    description: 'Set up customizable options for your products (flavors, toppings, sizes, etc.).',
    useFor: [
      'Add flavor options for Stuffed Cookies',
      'Add flavour options for Butter Tarts (Plain, Pecan)',
      'Add flavour options for Cake request (Cake Flavour group, category Custom Cakes)',
      'Add size and filling lists for the public cake request form',
      'Create topping choices for cakes',
      'Set up size options (small, medium, large)',
      'Add special instructions or customizations',
      'Link options to specific products or entire categories',
      'Set prices for different options'
    ],
    quickTip: 'Cake request form: category Custom Cakes, groups named Cake Size, Cake Flavour, Cake Filling. Turn options on/off with the eye. Product options also work with Special Menus.'
  },
  {
    id: 'homepage-editor',
    icon: HiSparkles,
    title: 'Homepage Editor',
    color: 'purple',
    link: '/admin/homepage-editor',
    description: 'Edit the welcome banner, Why Choose cards, section titles, show/hide blocks, and SEO. Not every decoration is editable — see the steps below for floating cookie icons and default cards.',
    useFor: [
      'Edit hero text (badge, title, description) and the storefront photo (browse or upload)',
      'Hide the floating cookie/cake/cupcake icons (Hero tab → uncheck “Show floating cookie…”)',
      'Hide or change the small “Best Seller” product card on the banner',
      'Manage Why Choose heading + cards (Features tab)',
      'Show/hide Featured Products, Popular Treats, Custom Cakes, Fundraisers, Fresh Kitchen, Testimonials (Sections tab)',
      'Edit customer testimonial quotes on Sections tab',
      'Featured product photos: Menu → star Featured; kitchen photos: Gallery → Featured',
      'Edit CTA (“Ready to Order?”) and SEO'
    ],
    quickTip: 'Why Choose photos: Features tab. Testimonial quotes: Sections tab. Floating cookie: Hero tab checkbox.',
  },
  {
    id: 'promos',
    icon: HiTag,
    title: 'Discounts & Promotions',
    color: 'pink',
    link: '/admin/promos',
    description: 'Create special offers and discounts for your customers.',
    useFor: [
      'Holiday specials (Christmas cakes, Valentine\'s Day)',
      '"Buy 6 get 1 free" deals',
      'Seasonal promotions',
      'The announcement banner at the top of your website'
    ],
    quickTip: 'The promo banner shows on every page - great for holiday announcements!'
  },
  {
    id: 'messages',
    icon: HiInbox,
    title: 'Contact messages',
    color: 'blue',
    link: '/admin/messages',
    description: 'Messages from your website contact form. You also get an email when someone submits.',
    useFor: [
      'See all contact form submissions in one place',
      'Filter by New, Read, or Replied',
      'Filter by subject text (Apply subject) — e.g. quote, Custom cake request',
      'Mark messages as read or replied',
      'Reply from portal: expand a message, type a reply, Send reply — customer gets email and status becomes Replied automatically',
      'Or use your own email app (mailto link) and tap Mark as Replied when done',
      'Admin notes: save internal notes on a thread (only visible in the portal)',
      'Create pay link: build line items from the menu (or custom lines), optional pickup date, then email the customer a secure link to pay on the Order page',
      'Delete old messages',
      'Click a message to expand and see full details and reply history'
    ],
    quickTip: 'Fastest path: expand → Send reply (works when you are logged in). Quote flow: customer picks subject "Price quote / Menu order" on /contact, you Send reply or Create pay link. After pay, message is marked Replied and they get a receipt. Wrong amount? Cancel unpaid order in Orders and send a new link.'
  },
  {
    id: 'orders',
    icon: HiClipboardList,
    title: 'Orders',
    color: 'green',
    link: '/admin/orders',
    description: 'See orders that come in from your website.',
    useFor: [
      'View new orders (sound + browser notification when a new order arrives)',
      'Watch the red Action Center on Orders: it keeps new order/message alerts visible even if you miss the sound',
      'Open **Never miss an order** (collapsed tips at the bottom of Orders) for sound, print, history, and auto-complete behavior',
      '**Click the number tiles** (Pending, Confirmed, Completed, …) to list those orders — the page switches history to **All orders** so past pickups are not hidden by **Today**',
      '**Day-to-day default:** history **Today** shows orders placed today, today\'s pickups, and **upcoming** Confirmed/Ready/Pending work. Use **Last 30 days** or **All orders** (or the green **All paid / picked up** button) for past orders',
      '**Auto-complete:** Paid orders still in Pending / Confirmed / Ready are moved to **Completed** after the **pickup date** has passed (Toronto calendar), so you do not have to tap Complete for every pickup',
      '**Completed vs Confirmed:** The **Completed** tile also counts **paid** orders whose **pickup date is before today** (Toronto) even if the row still says **Confirmed** or **Ready** — so past pickups are not hidden. **Confirmed** / **Ready** / **Pending** tiles exclude those (they are folded into **Completed** for the count).',
      'See the "Need attention" strip: new contact messages, closed today/tomorrow, promos ending soon, today\'s pickups, pending and custom cakes needing approval',
      'See customer details and what they ordered',
      'See each customer\'s flavour/option choice per item (e.g. stuffed cookie flavours, butter tart Plain/Pecan) — shown under each line and on the printed order',
      'See sandwich/customizable choices (bread, toppings) and custom cake details (size, flavor, filling, message) in the order and on print',
      'See pickup date and time for each order (expand an order to see "Pickup: [date] at [time]")',
      'Mark orders **Complete (Picked Up)** when the customer collects them (or wait for auto-complete the day after pickup date)',
      '**Website checkout ↔ Square:** The server creates the Square order **before** the customer can pay, then links the card charge to that Square order (with your **NB…** order number on the payment). That keeps **Square Orders / Payments** aligned with real website sales. If checkout says it is **still connecting to Square**, wait a few seconds and tap **Pay** again.',
      '**Old unpaid rows that were actually charged:** Your developer can run a **one-off backend script** (`reconcile-unlinked-square-payments.js`) that matches past orders to Square payments by **exact total + date window + email/note**, then (only when you set `RECONCILE_UNLINKED_APPLY=1`) marks them **paid** in the database so they show in Orders search. Always **dry-run first** (default) and review the printed list before applying.',
      '**Checkout:** “Continue to Payment” and **Pay** stay disabled while the server works; Pay uses a one-time lock and Square **idempotency** so a second tap with the same card token does not create a second charge.',
      '**Public site vs admin:** Shoppers see **short, polite** messages on the public Order/Menu pages (no raw server text) — they are asked to **call Tammy at (705) 503-4966** if something fails so they can still order. **You** still diagnose issues from **Admin → Orders**, Integrations, and server logs.',
      '**Completed** tile or filter: the list includes **Complete** status **and** paid orders still showing **Confirmed**, **Ready**, or **Pending** when their **pickup date** (calendar day) is **before today** in **Toronto** time — so past pickups are not hidden just because nobody tapped Complete yet.',
      'Order history: **Paid** website orders + Square for rolling ranges (**Today**, **Last 7 / 30 / 90 days**, **12 / 36 months**). The range is mainly **when the order was placed**; the list also pulls in **paid** rows whose **pickup date** falls in that range even if they were **placed earlier** (so past pickups are not missing). **All orders (since 2025)** is the full archive: **paid and unpaid** website rows in that window (newest first; **Load more**). The server loads the merged list in **chunks** so paid rows are not cut off at an old row cap. Use the green **All paid / picked up** button to jump to **Completed** (paid + picked up / past pickup) and **auto-load every page** until nothing is left. Shorter presets hide generic unpaid checkouts so the list stays manageable; **custom cakes** (**Needs Approval**) and **contact quotes** still appear when unpaid. Use **Last 36 months** if you only need recent placement dates. When you open or refresh Orders, the server **re-checks Square** for rows that were **charged** but not saved as **paid** here: Square order **COMPLETED**, or an **OPEN** order whose **tender payment** is successful, or a stored **Square payment ID** with successful payment status — all count as paid. Reconcile looks back up to **~2 years** from the end of your selected range (not only the “last N days” start), so stuck rows still fix when you view **Today**. Rows with **no** Square order ID **and** **no** Square payment ID cannot be auto-matched — use **Mark paid (offline)** or ensure checkout saves the Square link. **Integrations** must use the same Square environment (**production** vs sandbox) as real checkouts. **Completed** (tile or filter) loads that cohort for your history window from the server — not only the newest mixed-status page. **Numbers on the status tiles** are **exact full-database** counts for **paid** orders (not capped), so they can be higher than rows in your date window. Use **Load more** for older rows in range. Search only searches already-loaded rows.',
      'Open any order and use "Payment & transaction details" to see transaction ID, amount, and Square receipt link when you need proof',
      'Understand reminder emails (customers who don’t complete payment get one friendly email with a link to pay later)',
      '**Custom cakes:** When someone submits a request, **you get an email** and the **customer gets a “we received your request”** email (both require mail + customer email for the latter). **Approve custom cake** sends them **Pay now**. If you **cancel** an order, the customer gets an email — wording is different for **Needs Approval** (declined request) vs other stages; if they already paid, the email mentions contacting you about a refund',
      '**Regular website checkout:** The customer gets an **order confirmation email only after card payment succeeds** (same moment you get your **new order** email). Reaching checkout or entering an email does **not** send confirmation by itself.',
      'When you tap **Mark Ready for pickup**, the customer gets an **email** automatically (same Mailgun/SMTP as order confirmations — must be configured on the server)',
      '**Sound (any laptop/desktop):** First tap or keypress on Orders (or **Test sound**) unlocks a sharper alarm-style chirp. New-order alerts use your **paid website order count** so Square POS sync won’t cause false or missed alerts. Use Chrome, Edge, Firefox, or Safari in a **normal tab**—not an in-app browser. Volume icon ON; unmute tab; lock/info icon → site settings → **Sound** allowed if needed; system volume up.',
      '**Print (any laptop/desktop):** **Print order** uses a hidden print sheet first, then your browser’s print dialog. Allow **pop-ups** for this site if the dialog never appears. **Auto-print** (🖨️ button) opens the print dialog when a **new website order** is detected — preference is **saved on this device** so it stays on after you close the browser. Same on Windows, Mac, or Chrome OS.',
      '**Print fallback:** Browsers usually cannot silently send print jobs without a dialog. If auto-print does not start, use the red Action Center **Print** button on Orders.'
    ],
    quickTip: 'Check this daily for new orders. Tap the page once or Test sound; volume on. Turn on Auto-print once per device if you want the print dialog when new orders arrive. Print order opens your browser’s print dialog—allow pop-ups if needed. Use a full browser tab, not a web view inside another app.'
  },
  {
    id: 'customers',
    icon: HiUsers,
    title: 'Customers',
    color: 'blue',
    link: '/admin/customers',
    description: 'Your customer list - everyone who has ordered from the website. Add notes and tags (e.g. allergy, VIP) per customer.',
    useFor: [
      'See customer names and contact info',
      'Add notes or tags (e.g. "nut allergy", "VIP", "Corporate") - click the pencil next to a customer',
      'View their order history',
      'Reach out to repeat customers'
    ],
    quickTip: 'Use notes and tags for allergies or special instructions so you see them when preparing orders.'
  },
  {
    id: 'gallery',
    icon: HiPhotograph,
    title: 'Gallery',
    color: 'pink',
    link: '/admin/gallery',
    description: 'Your photo gallery that shows on the website. Use Gallery to add or edit gallery photos (titles, categories, featured). Use All Images to see every image and delete files from storage.',
    useFor: [
      'Upload beautiful photos of your cakes and treats',
      'Showcase custom orders you\'ve made',
      'Remove old or blurry photos',
      'Browse all uploaded images (even unused ones!)',
      'Use emojis 🎂🧁🍪 for quick icons and labels'
    ],
    quickTip: 'Good photos attract new customers! When uploading, give images descriptive names like "chocolate-birthday-cake" to find them easily later.'
  },
  {
    id: 'images',
    icon: HiViewGrid,
    title: 'All Images',
    color: 'purple',
    link: '/admin/images',
    description: 'One place to see every image (menu, gallery, special menus, events, storage). See where each image is used—including Promos, Homepage, Site overrides, and “Why Choose” feature cards—and which uploads are unused (safe to delete). Rotate or permanently delete files from storage. On a menu item, pick Original photo or Fill square for the product page (cards stay square).',
    useFor: [
      'See all images and where each is used (Menu, Gallery, Events, Promos, Homepage, Site overrides)',
      'Click a "Used in" label to jump to that page (e.g. menu item, event editor, Homepage Editor)',
      'Filter by Unused only to find uploads safe to delete',
      'Filter by Menu, Gallery, Special Menus, Events, or In storage',
      'Rotate an image 90° if it appears sideways (for images in storage)',
      'When uploading: choose Square, Portrait, Landscape, Wide, or Original before crop — or Skip crop to keep the whole file',
      'On Menu → Edit item: click the preview to set the focal point; pick Original photo (full file on the cake page) or Fill square',
      'Permanently delete a file from storage (modal shows if it’s still used or safe to delete)'
    ],
    quickTip: 'Menu cards stay square. Open a cake on the website to see the full photo. Want it square there too? Edit the item and tap Fill square under the preview. Photo sideways? Rotate 90° here or on the item.'
  },
  {
    id: 'events',
    icon: HiSparkles,
    title: 'In Store Events',
    color: 'pink',
    link: '/admin/event-categories',
    description: 'Create and manage in-store events like Decorating Classes, Birthday Parties, and Tastings.',
    useFor: [
      '📝 CREATE EVENT: Click "New Event" → Add name, description, image → Save',
      '✏️ EDIT EVENT: Find event → Click pencil icon → Make changes → Save',
      '📄 ADD LANDING PAGE: Edit event → Toggle "Has Landing Page" ON → Fill in details',
      '🏠 SHOW ON HOMEPAGE: Edit event → Toggle "Show on Homepage" → Add tagline → Save',
      '👁️ HIDE/SHOW: Click eye icon to toggle visibility',
      '🗑️ DELETE: Click trash icon → Confirm (cannot be undone!)'
    ],
    quickTip: 'Landing pages are like mini websites for your events! Add date, time, price, photos, and a "Book Now" button. Events with "Show on Homepage" ON appear in the auto-scrolling carousel!'
  },
  {
    id: 'campaigns',
    icon: HiLink,
    title: 'Fundraiser campaigns',
    color: 'purple',
    link: '/admin/campaigns',
    description:
      'School or partner fundraisers: one stable link for many buyers. Each person gets their own order and Tammy is notified when they pay (same as regular website checkout).',
    useFor: [
      'Create a campaign: pick one menu product (simple items only — no sandwiches/drinks/soups/stuffed cookies/butter tarts). The item must be **Available** on the Menu or buyers see a message instead of checkout',
      '**Turn on / off:** use the **On** checkbox in the table, or **Campaign is on (Active)** in the form — when off, links show “not available” (no need to delete)',
      '**Status column:** Live (on + inside dates), Paused (off), Scheduled (before start), Ended (after end)',
      '**Product photo** comes from **Menu** for that item; **Banner image** is optional — paste a direct **https://** image URL (no homepage editor)',
      '**Show headline on public page** (default on): turn off when your **banner** is a full poster with the name and wording — you can still leave **Hero** / **Thank-you** blank',
      '**Hero text** (optional): a few sentences under the title (when the headline is on) — who benefits, order-by date, pickup at the bakery',
      '**Thank-you / partner** (optional): organizer contact, pickup reminder, or thank the school/team (shows in a box above the product)',
      'On **Fundraiser campaigns**, use **Insert school fundraiser** or **Insert short & simple** to paste starter text, then replace [brackets]',
      'Copy the **Landing** URL for the public page (`/campaign/your-slug`); **Checkout** skips to cart + pay (`/order?campaign=your-slug`)',
      'Optional **max units** caps how many can sell; sold count updates when orders are **paid**',
    ],
    quickTip:
      'Use campaigns for “many people, one link.” Pause with the On switch instead of deleting. Note: a menu item used by a campaign cannot be deleted until you point the campaign at a different product or delete the campaign (Off alone is not enough).',
  },
  {
    id: 'articles',
    icon: HiClipboardList,
    title: 'Articles',
    color: 'blue',
    link: '/admin/articles',
    description: 'Write helpful articles/guides to attract customers through Google search.',
    useFor: [
      'A few original guides from real shop experience (how you take cake orders, pickup hours)',
      'Leave weekly AI drafts unpublished — Google treats mass similar pages as spam',
      'Publish/unpublish with one click',
      'Preview articles before publishing',
    ],
    quickTip: 'A few original guides are enough. Do not publish a new article every day — Google treats mass AI pages as spam. Prefer real shop experience (how you take cake orders, pickup hours).'
  },
  {
    id: 'google-search',
    icon: HiSearch,
    title: 'Show up on Google',
    color: 'indigo',
    link: '/bakery-in-barrie',
    description:
      'Your list only. The top row for “bakeries in Barrie” is Google’s map list. Humberto handles Search Console and directory listings.',
    useFor: helpShowUpOnGoogleSteps(),
    quickTip:
      'Google reads the Q&A on /bakery-in-barrie (not a chat box). Maps row still needs reviews + weekly Google posts. Do not publish a new article every day.',
  },
  {
    id: 'integrations',
    icon: HiGlobe,
    title: 'Integrations',
    color: 'indigo',
    link: '/admin/integrations',
    description: 'Connect your website to Square POS and other services. Product feed URLs (Google Merchant + Meta catalog) use full https image links and pickup-only shipping hints. The public site also links to your Uber Eats store for delivery (separate from website checkout).',
    useFor: [
      'Square POS - See orders from both website AND in-store',
      'Google Analytics (GA4) - Open Integrations → expand Google Analytics for reports link; status shows when tracking is enabled',
      'Facebook Pixel - Run Facebook/Instagram ads',
      'Product feeds - Copy /api/feed/google.xml and /api/feed/meta.xml into Google Merchant Center and Meta Commerce (pickup in store; no delivery)',
      'Uber Eats — customers see **Order on Uber Eats** on the live site (footer, homepage, Order page); that is delivery via Uber. Website cart/checkout remains **pickup only**',
      'Check connection status for each integration'
    ],
    quickTip: 'Square is connected! Your POS orders sync automatically. Push to Square links to existing items by name so you don\'t get duplicates. For Google Shopping / Meta catalog: use the feed URLs on Integrations — they list pickup-only (Canada, $0) and work best when products have photos. If you have duplicate items in Square (e.g. 2 or 3 of the same): Admin → Integrations → connect Square if needed, then click "Remove duplicates in Square" to fix in one click.'
  },
  {
    id: 'settings',
    icon: HiCog,
    title: 'Settings',
    color: 'gray',
    link: '/admin/settings',
    description: 'Your bakery\'s basic information. Add holiday or one-off closure dates so customers cannot choose those days for pickup.',
    useFor: [
      'Update business hours',
      'Add holiday/one-off closures (e.g. Christmas) - customers see those dates as closed on the Order page',
      'Change phone number or email',
      'Update your address',
      'Edit "About Us" text'
    ],
    quickTip: 'Add closure dates in Settings so the Order page blocks checkout on those dates (same as weekly closed days like Sunday).'
  },
  {
    id: 'tax-reports',
    icon: HiDocumentReport,
    title: 'Tax & Reports',
    color: 'green',
    link: '/admin/tax-reports',
    description: 'Year-end tax info for your accountant and minor tax display settings.',
    useFor: [
      'Run a tax report for a date range (e.g. Jan 1 – Dec 31)',
      'Download CSV for your accountant (sales, HST collected, order count)',
      'See how taxes are set up (6+ pieces and cakes exempt; 13% HST otherwise)',
      'Change the tax label customers see (e.g. HST (13%), Tax) – cosmetic only',
      'Set your HST/Business number for the CSV export'
    ],
    quickTip: 'You cannot change tax rules or rate here. To change anything about how tax is calculated, contact your developer.'
  }
];

// Color classes for consistent styling
const colorClasses: Record<string, { bg: string; icon: string; border: string }> = {
  purple: { bg: 'bg-purple-50', icon: 'text-purple-500', border: 'border-purple-200' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-500', border: 'border-amber-200' },
  pink: { bg: 'bg-pink-50', icon: 'text-pink-500', border: 'border-pink-200' },
  green: { bg: 'bg-green-50', icon: 'text-green-500', border: 'border-green-200' },
  blue: { bg: 'bg-blue-50', icon: 'text-blue-500', border: 'border-blue-200' },
  indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-500', border: 'border-indigo-200' },
  gray: { bg: 'bg-gray-50', icon: 'text-gray-500', border: 'border-gray-200' },
};

// Quick tips section
const quickTips = [
  { emoji: '🔴', tip: 'Sold out?', action: 'Go to Menu → Click Hide on the item' },
  { emoji: '🍰', tip: 'New cake?', action: 'Go to Menu → Click + Add Item' },
  { emoji: '🎂', tip: 'Customer ordering a custom cake?', action: 'They use /cake-request. Change size/flavour/filling lists in Product Options (Custom Cakes). Print the counter form at /cake-request/print.' },
  { emoji: '📁', tip: 'New category (Valentines, Seasonal, Gluten Free)?', action: 'Go to Categories → Add Category → name + optional emoji' },
  { emoji: '🍪', tip: 'Cookie flavours not showing?', action: 'Product Options → Stuffed Cookie Flavors → turn eye ON for each flavour' },
  { emoji: '🎄', tip: 'Holiday sale?', action: 'Go to Discounts & Promotions → Create a new discount' },
  { emoji: '📸', tip: 'New photos?', action: 'Go to Gallery → Upload them (give descriptive names!)' },
  { emoji: '🕐', tip: 'Change hours?', action: 'Go to Settings → Edit hours' },
  { emoji: '💝', tip: 'Seasonal menu?', action: 'Go to Special Menus → Create new → Add products' },
  { emoji: '🖼️', tip: 'Change homepage content?', action: 'Go to Homepage Editor → Edit any section' },
  { emoji: '🔍', tip: 'Find an image?', action: 'Click any image field → Browse Images → Filter by source' },
  { emoji: '😊', tip: 'Need an emoji?', action: 'Click any image field → Browse Images → Emojis tab' },
  { emoji: '🎓', tip: 'Hosting a class?', action: 'Go to In Store Events → Create event → Build landing page' },
  { emoji: '⭐', tip: 'Feature event on homepage?', action: 'In Store Events → Edit → Toggle "Show on Homepage"' },
  { emoji: '📝', tip: 'Improve Google ranking?', action: 'Help → Show up on Google. Ask for a review at pickup. Answer Google Maps questions the same way as /bakery-in-barrie. Do not mass-publish daily articles.' },
  { emoji: '⭐', tip: 'Want more reviews?', action: 'Ask happy customers to click Google Review on homepage!' },
  { emoji: '🔗', tip: 'Check Square?', action: 'Go to Integrations → See connection status' },
  { emoji: '📦', tip: 'Duplicate items in Square (e.g. 2 or 3 of the same)?', action: 'Admin → Integrations → connect Square if needed, then click "Remove duplicates in Square".' },
  { emoji: '🧾', tip: 'Year-end for accountant?', action: 'Go to Tax & Reports → Run report → Download CSV' },
  { emoji: '📬', tip: 'Contact form messages?', action: 'Sales & Customers → Contact messages. Send reply from the portal, or use your email app and Mark as Replied.' },
  { emoji: '📋', tip: 'Copy a menu item or special menu?', action: 'Menu or Special Menus → click the duplicate (copy) icon on the item or collection.' },
  { emoji: '💰', tip: 'Raise all prices by 5%?', action: 'Menu → Bulk actions → choose category (or All), enter 5, click Apply.' },
  { emoji: '📅', tip: 'Store closed on a specific date (e.g. Christmas)?', action: 'Settings → Holiday/one-off closures → Add date + optional label.' },
  { emoji: '🏷️', tip: 'Remember allergy or VIP per customer?', action: 'Customers → click pencil next to the customer → add Tags (e.g. nut allergy, VIP) and Notes.' },
  { emoji: '🌐', tip: 'View the live website?', action: 'Click "View site" in the top-right of the admin panel (opens in a new tab).' },
  { emoji: '🛵', tip: 'Customer asking about delivery / Uber Eats?', action: 'The site has green Order on Uber Eats buttons; website checkout is pickup only. If the Uber store link changes, ask your developer to update the URL in code.' },
];

export default function Help() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">🍰 Admin Guide for Tammy</h1>
        <p className="text-purple-100">
          Everything you need to know about managing your bakery website.
          Click on any section below to learn more!
        </p>
      </div>

      {/* Tammy's workflow - add or change items */}
      <div className="bg-white rounded-xl border-2 border-primary-200 overflow-hidden">
        <button
          onClick={() => toggleSection('tammy-workflow')}
          className="w-full p-4 flex items-center justify-between hover:bg-primary-50/50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary-100">
              <HiClipboardList className="w-6 h-6 text-primary-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Tammy&apos;s workflow – Add or change items</h3>
              <p className="text-sm text-gray-500">Menu items, cookie flavours, butter tarts – and why flavours weren&apos;t showing</p>
            </div>
          </div>
          {expandedSection === 'tammy-workflow' ? (
            <HiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
          ) : (
            <HiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
        </button>
        {expandedSection === 'tammy-workflow' && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="pt-4 space-y-4">
              <div className="bg-primary-50/50 rounded-lg p-4 border border-primary-200">
                <h4 className="font-semibold text-gray-900 mb-2">📝 Add or change menu items (cakes, cookies, tarts, etc.)</h4>
                <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                  <li><strong>Add a new item:</strong> Sidebar → <strong>Menu</strong> → purple <strong>+ Add Item</strong> → fill Name, Price, Category, Description, image → check Featured / Popular / Online Ordering if you want → check <strong>🌾 Gluten free</strong> for GF items (customers see a badge and can filter “Gluten free only”) → <strong>Save</strong>.</li>
                  <li><strong>Edit an item:</strong> Menu → find the item → click the <strong>pencil icon</strong> → change what you need → <strong>Save</strong>. Changes show on the site right away.</li>
                  <li><strong>Product photos:</strong> Menu cards stay square. When someone opens the cake, they see the <strong>full original photo</strong>. Under the preview, pick <strong>Original photo</strong> or <strong>Fill square</strong>. Upload lets you crop Square / Portrait / Landscape / Original, or Skip crop. Click the preview to set the focal point; use Rotate 90° if it is sideways.</li>
                  <li><strong>Add or change categories:</strong> Sidebar → <strong><Link to="/admin/categories" className="text-primary-600 hover:underline">Categories</Link></strong> → Add Category (e.g. Valentines, Seasonal, Gluten Free) or edit name/icon/order. Hidden categories don’t show on the menu.</li>
                  <li><strong>Hide from online ordering:</strong> Edit the item → uncheck <strong>Available for Online Ordering</strong> → Save.</li>
                </ul>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h4 className="font-semibold text-gray-900 mb-2">🍪 Cookie flavours – why yours weren&apos;t showing (fixed)</h4>
                <p className="text-sm text-gray-700 mb-2">You had <strong>15 stuffed cookie flavours</strong> in the system, but <strong>14 were turned off</strong> (hidden). The site only shows flavours that are &quot;available&quot;. We&apos;ve turned <strong>all 15 on</strong> in the database so they all show on the site now: Oreo, Biscoff, Kinder Bueno, Mars, Nutella, Peanut buttercup, Kit Kat, Red velvet cookies &amp; crème, Red velvet oreo, Twix, Snickers, Skor, S&apos;mores, Brownie, Caramilk.</p>
                <p className="text-sm text-gray-700 mb-2"><strong>To add or change flavours yourself:</strong> Sidebar → <strong><Link to="/admin/product-options" className="text-primary-600 hover:underline">Product Options</Link></strong> → open the group <strong>Stuffed Cookie Flavors</strong> (category Stuffed Cookies). There you can:</p>
                <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 mb-2">
                  <li><strong>Add a flavour:</strong> Add Option → enter name → Save.</li>
                  <li><strong>Edit a name:</strong> pencil icon next to the flavour.</li>
                  <li><strong>Hide a flavour from the site:</strong> click the <strong>eye icon</strong> so it&apos;s off (grey) – customers won&apos;t see it. Click again to show it.</li>
                  <li><strong>Remove a flavour:</strong> trash icon (permanent).</li>
                </ul>
                <p className="text-xs text-gray-500">The menu category note and the flavour popup when customers add stuffed cookies to the cart both use this list. Turn flavours on/off with the eye whenever you want.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">🥧 Butter tart flavours (Plain / Pecan)</h4>
                <p className="text-sm text-gray-700">Customers choose Plain or Pecan when they add Butter Tart or 6 Butter Tarts. To add more options (e.g. Raisin): <strong>Product Options</strong> → Create New Option Group → category <strong>Tarts</strong>, name like &quot;Flavors&quot; → add options. If you don&apos;t set any, the site uses Plain and Pecan by default.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Tips Card */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <HiLightBulb className="w-5 h-5 text-amber-500" />
          Quick Tips - Common Tasks
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {quickTips.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-amber-100">
              <div className="flex items-start gap-2">
                <span className="text-lg">{item.emoji}</span>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{item.tip}</p>
                  <p className="text-xs text-gray-600">{item.action}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Latest updates & FAQ (cart, stuffed cookies, tax) */}
      <div className="bg-white rounded-xl border border-blue-200 overflow-hidden">
        <button
          onClick={() => toggleSection('latest-updates')}
          className="w-full p-4 flex items-center justify-between hover:bg-blue-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-100">
              <HiSparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Latest updates &amp; FAQ</h3>
              <p className="text-sm text-gray-500">Cart, stuffed cookie flavours, tax rules</p>
            </div>
          </div>
          {expandedSection === 'latest-updates' ? (
            <HiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
          ) : (
            <HiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
        </button>
        {expandedSection === 'latest-updates' && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="pt-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">🛒 Can&apos;t delete items from the cart?</h4>
                <p className="text-sm text-gray-700 mb-2">If the trash button doesn&apos;t remove an item:</p>
                <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                  <li>Refresh the page (F5 or reload), then try removing again. We fixed a bug so every cart line has a proper ID.</li>
                  <li>If it still happens, have the customer go back to the menu, clear the cart, and add items again.</li>
                </ol>
                <p className="text-xs text-gray-500 mt-2">If it keeps happening, tell Humberto!</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">🍪 Where do customers select stuffed cookie flavours?</h4>
                <p className="text-sm text-gray-700 mb-2">Customers pick flavours when they click <strong>Add to Cart</strong> on Stuffed Cookies on the Menu – a modal opens saying &quot;Pick your flavours below, then click Add to Cart.&quot; In the cart they can click <strong>Change flavours</strong> under the item to change the selection before checkout.</p>
                <p className="text-sm text-gray-700 mt-2"><strong>You can add or remove flavours:</strong> Admin → <strong>Product Options</strong> → find the group for category &quot;Stuffed Cookies&quot; (e.g. &quot;Stuffed Cookie Flavors&quot;) → add, edit, or remove options. The menu note and ordering modal update automatically with whatever flavours you configure.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">🥧 Where do customers pick butter tart flavour (Plain or Pecan)?</h4>
                <p className="text-sm text-gray-700 mb-2">When they click <strong>Add to Cart</strong> on Butter Tart or 6 Butter Tarts on the Menu, a modal opens to choose <strong>Plain</strong> or <strong>Pecan</strong>. In the cart they can click <strong>Change flavour</strong> under the item to change it. You can add more options (e.g. Raisin) in Admin → <strong>Product Options</strong> → create a group for category &quot;Tarts&quot; with a &quot;Flavors&quot; or &quot;Variety&quot; name.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">🧾 How is tax applied on orders?</h4>
                <p className="text-sm text-gray-700">We follow Ontario CRA rules: <strong>6 or more baked items</strong> (e.g. 6 cookies, 1 dozen = 12 pieces) = no HST on those. <strong>Cakes</strong> = no tax. <strong>Sandwiches &amp; savory</strong> = always taxable. Otherwise 13% HST on the taxable part only. Order summary and Admin show &quot;Taxable amount (before HST)&quot; when tax applies, plus: &quot;6+ baked items or cakes exempt. Sandwiches always taxable.&quot;</p>
                <p className="text-sm text-gray-700 mt-2">If you’re not sure whether tax applies to a specific product, use <strong>Menu → edit the item → HST (tax) for this item</strong>: choose <strong>Always taxable</strong> or <strong>Never taxable</strong> to override the category rule.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Sections */}
      <div className="space-y-4">
        <h2 className="font-semibold text-gray-900 text-lg">📋 What Each Section Does</h2>
        
        {helpSections.map((section) => {
          const Icon = section.icon;
          const colors = colorClasses[section.color];
          const isExpanded = expandedSection === section.id;

          return (
            <div 
              key={section.id}
              className={`bg-white rounded-xl border ${colors.border} overflow-hidden transition-shadow hover:shadow-md`}
            >
              {/* Header - clickable */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${colors.bg}`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-sm text-gray-500">{section.description}</p>
                  </div>
                </div>
                {isExpanded ? (
                  <HiChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <HiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">✅ Use it for:</h4>
                    <ul className="space-y-2">
                      {section.useFor.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="text-green-500 mt-0.5">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>

                    {/* Detailed instructions for Homepage Editor */}
                    {section.id === 'homepage-editor' && (
                      <div className="mt-6 space-y-4">
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <h5 className="font-semibold text-gray-900 mb-3">🏠 Homepage Editor Tabs:</h5>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>Hero Section:</strong> Title, description, badge, storefront photo (browse/upload). Uncheck <strong>Show floating cookie, cake, and cupcake icons</strong> to remove those decorations. Uncheck the small product card or change its photo/text.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>Features:</strong> Manage "Why Choose Nanny B's Bakery?" feature cards (add, edit, delete, hide)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>Sections:</strong> Edit titles, descriptions, and visibility for Featured Products, Popular Treats, and Custom Cakes</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>Featured & Popular tiles:</strong> Which products appear comes from <strong>Menu</strong> (⭐ Featured Gallery / 🔥 Popular Treat). On the website, tapping a tile opens the Menu with that item opened and highlighted—no extra admin setting needed.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>CTAs:</strong> Edit the "Ready to Order?" section (title, description, button text)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">•</span>
                              <span><strong>SEO:</strong> Add meta title, description, keywords, and Open Graph image for better Google rankings</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h5 className="font-semibold text-gray-900 mb-3">🖼️ How to Edit or Remove Images:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">1.</span>
                              <span>Go to <strong>Homepage Editor</strong> → <strong>Features</strong> tab</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">2.</span>
                              <span>Find the feature you want to change (e.g., "Fresh Daily" for the donuts image)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">3.</span>
                              <span>Click the <strong>Edit</strong> (pencil) icon</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">4.</span>
                              <span><strong>To change the image:</strong> Use the 3 tabs: "Browse Images" (pick from ALL your images - Menu, Gallery, Events, Uploads), "Upload" (add new from device), or "Paste URL". You can even pick emojis 🎂!</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">5.</span>
                              <span>Click <strong>"Update"</strong> to save</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">6.</span>
                              <span><strong>To remove the feature entirely:</strong> Click the <strong>Delete</strong> (trash) icon instead</span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h5 className="font-semibold text-gray-900 mb-3">➕ Adding a New Feature:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">1.</span>
                              <span>Go to <strong>Homepage Editor</strong> → <strong>Features</strong> tab → Click <strong>"New Feature"</strong> button</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">2.</span>
                              <span>Enter a title (e.g., "Fresh Daily", "Made with Love")</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">3.</span>
                              <span>Write a description (e.g., "All our baked goods are made fresh daily...")</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">4.</span>
                              <span><strong>Add an image:</strong> Use "Browse Images" tab to pick from ALL your existing images (Menu, Gallery, Events, Uploads), "Upload" tab to add new from your device, or "Paste URL" tab for web links. Tip: You can also use emojis 🎂🧁!</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">5.</span>
                              <span>Choose an icon (Cake, Shopping Bag, Heart, Star, or Sparkles)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">6.</span>
                              <span>Set the display order (lower numbers appear first)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">7.</span>
                              <span>Click <strong>"Create"</strong></span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h5 className="font-semibold text-gray-900 mb-3">👁️ Hiding Features:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">1.</span>
                              <span>Find the feature you want to hide</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">2.</span>
                              <span>Click the <strong>eye icon</strong> (👁️) to hide it</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">3.</span>
                              <span>The feature will disappear from your homepage but won't be deleted</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">4.</span>
                              <span>Click the eye icon again to show it again later</span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <h5 className="font-semibold text-gray-900 mb-3">💡 Important Notes:</h5>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Default features:</strong> You start with 3 features: "Custom Cakes", "Fresh Daily" (with donuts), and "Made with Love". You can edit or remove any of these!</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Images:</strong> You can upload new images, browse your existing gallery, or paste image URLs. Images are automatically optimized!</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Display Order:</strong> Lower numbers appear first. If you want a feature to appear first, give it a lower number (like 0 or 1)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Removing the donuts:</strong> To remove the mini donuts image, edit the "Fresh Daily" feature and change the image to something else, or delete the feature entirely</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Detailed instructions for Special Menus */}
                    {section.id === 'collections' && (
                      <div className="mt-6 space-y-4">
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <h5 className="font-semibold text-gray-900 mb-3">📋 How to Create a Special Menu:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">1.</span>
                              <span>Click <strong>"New Special Menu"</strong> button</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">2.</span>
                              <span>Enter collection name (e.g., "Valentine's Day Menu")</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">3.</span>
                              <span><strong>Add a banner image:</strong> Use "Browse Images" to pick from existing images, "Upload" to add new from your device, or "Paste URL" for web links. This image shows as a banner on the menu page and as a card on your homepage.</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">4.</span>
                              <span>Add a description (optional but recommended)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">5.</span>
                              <span>Set start/end dates if you want it to auto-show/hide (optional)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-purple-600">6.</span>
                              <span>Click <strong>"Create"</strong></span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h5 className="font-semibold text-gray-900 mb-3">➕ Adding Products to a Special Menu:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">1.</span>
                              <span>Click the collection to expand it (or click the down arrow)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">2.</span>
                              <span>Click <strong>"+ Add Items"</strong></span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">3.</span>
                              <span>Check existing products from the list, then click <strong>"Add X Items"</strong></span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">4.</span>
                              <span><strong>Need to create a new product?</strong> Go to <strong>Menu → Add Item</strong> to create it first, then come back here to add it to the collection</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-green-600">5.</span>
                              <span>Products you add will appear in the collection. You can remove them by hovering and clicking the X button.</span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h5 className="font-semibold text-gray-900 mb-3">🎛️ Making Special Menus Live:</h5>
                          <ol className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">1.</span>
                              <span>Find your collection in the list</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">2.</span>
                              <span>Click the toggle switch to turn it <strong>ON</strong> (it will turn green)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">3.</span>
                              <span>When ON, the collection appears on your website homepage and customers can view it</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="font-semibold text-blue-600">4.</span>
                              <span>Click the toggle again to turn it <strong>OFF</strong> and hide it from the website</span>
                            </li>
                          </ol>
                        </div>

                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <h5 className="font-semibold text-gray-900 mb-3">💡 Important Notes:</h5>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Banner images:</strong> Upload a beautiful banner image - it shows as a large banner on the collection page and as a card image on your homepage</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Product options:</strong> When you add products to a collection, their customizable options (like flavors, toppings) automatically work - no extra setup needed!</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Dates:</strong> Set start/end dates to automatically show collections during specific seasons (e.g., Valentine's Day: Feb 1-14)</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Homepage:</strong> Special Menus with "Show on homepage" enabled appear in the "Seasonal Specials" section on your homepage</span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <span><strong>Creating products:</strong> To create a new product, go to <strong>Menu → Add Item</strong>. After creating it, come back to Special Menus to add it to your menu.</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {section.id === 'integrations' && (
                      <div className="mt-6 space-y-4">
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h5 className="font-semibold text-gray-900 mb-2">📈 Website traffic (Google Analytics)</h5>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>
                                Go to <strong>Integrations</strong> → open <strong>Google Analytics (GA4)</strong>. If it says{' '}
                                <strong>Connected</strong>, your public site is already counting visits. Click{' '}
                                <strong>Open Google Analytics</strong> for reports.
                              </span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>
                                In Google Analytics: <strong>Reports</strong> → <strong>Traffic acquisition</strong> shows where
                                visitors came from (search, social, direct, etc.). <strong>Pages and screens</strong> shows popular pages.
                              </span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>
                                If it says <strong>Not Connected</strong>, ask your developer to set{' '}
                                <code className="text-xs bg-green-100 px-1 py-0.5 rounded">VITE_GA_MEASUREMENT_ID</code> on the server and redeploy.
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                          <h5 className="font-semibold text-gray-900 mb-2">🛵 Uber Eats on the public website</h5>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex gap-2">
                              <span className="text-indigo-600 mt-0.5">•</span>
                              <span>
                                <strong>Pickup vs delivery:</strong> The <strong>Order Online</strong> flow on your site is for <strong>in-store pickup</strong> only. Customers who want <strong>delivery</strong> use the green <strong>Order on Uber Eats</strong> buttons (homepage, footer Quick Links, Order page, and bottom CTA).
                              </span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-indigo-600 mt-0.5">•</span>
                              <span>
                                The link opens your official Uber Eats storefront in a new tab (delivery mode). You do <strong>not</strong> change that link from the admin — if Uber ever gives you a new store URL, ask your developer to update the constant in the site code.
                              </span>
                            </li>
                            <li className="flex gap-2">
                              <span className="text-indigo-600 mt-0.5">•</span>
                              <span>
                                Optional: For official co-marketing badge artwork, use Uber&apos;s merchant resources (e.g. merchants.ubereats.com learning center / co-marketing tools). The site uses a text button in Uber Eats green until you replace it with an approved image.
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {section.quickTip && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>💡 Tip:</strong> {section.quickTip}
                        </p>
                      </div>
                    )}

                    <Link
                      to={section.link}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      Go to {section.title} →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Need Help Footer */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 text-center">
        <HiPhone className="w-10 h-10 text-blue-500 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900 mb-2">Need More Help?</h3>
        <p className="text-gray-600 mb-4">
          Questions? Something not working? Call Humberto anytime!
        </p>
        <a
          href="tel:+1234567890"
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <HiPhone className="w-5 h-5" />
          Call for Help
        </a>
      </div>
    </div>
  );
}

